"use client";

import { useRef, useState, useEffect, useContext } from "react";
import { useParams } from "next/navigation";
import { Loader } from "chingari-components";
import styles from "./ProfilePageRevamp.module.scss";

import StickyProfileHero from "./components/StickyProfileHero";
import About from "./components/About";
import Expertise from "./components/Expertise";
import ReviewsSection from "./components/ReviewsSection";
import PricingCard from "./components/PricingCard";
import { AppContext } from "@/context/AppContext";
import { getNonAuthToken } from "@/services/common.service";
import { getUserInfo } from "@/services/profile.service";
import { ProfileInfoDataInterface } from "@/modules/Astro/UserProfile/ProfilesInfo/profileTypes";

export default function ProfilePageRevamp() {
  const [collapsed, setCollapsed] = useState(true);
  const cardRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const params = useParams();
  const userName = params?.roomType as string;
  const context = useContext(AppContext);
  const { nonAuth, setNonAuth } = context ?? {};
  const { token } = context?.auth ?? {};

  const [userData, setUserData] = useState<ProfileInfoDataInterface>();
  const [isLoading, setIsLoading] = useState(false);

  const fetchUserData = async (tokenData?: string) => {
    try {
      const nonAuthToken = tokenData ?? nonAuth;
      setIsLoading(true);
      const data = await getUserInfo(
        userName,
        token ? token : (nonAuthToken as string)
      );
      setUserData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!userName || isLoading) return;

    if (!nonAuth) {
      // Always fetch nonAuth token first before calling profile-info
      console.log("hit1");
      setIsLoading(true);
      void getNonAuthToken()
        .then((data) => {
          setNonAuth?.(data);
          if (!userData) void fetchUserData(data);
        })
        .catch((err) => {
          console.error(err);
          setIsLoading(false);
        });
    } else if (!userData) {
      console.log("hit2");
      void fetchUserData();
    }
  }, [userName, nonAuth, token]);

  useEffect(() => {
    const isMobile = () => window.innerWidth < 600;
    const scrollContainer = document.getElementById("astro") || window;

    const handleScroll = () => {
      if (isMobile()) {
        setCollapsed(true);
      } else {
        const scrollTop =
          "scrollTop" in scrollContainer
            ? scrollContainer.scrollTop
            : window.scrollY;
        setCollapsed(scrollTop > 20);
      }

      if (cardRef.current && contentRef.current) {
        const cardRect = cardRef.current.getBoundingClientRect();
        const contentRect = contentRef.current.getBoundingClientRect();
        const clipTop = cardRect.bottom + 24 - contentRect.top;
        const clipValue = clipTop > 0 ? clipTop : 0;
        contentRef.current.style.setProperty("--clip-top", `${clipValue}px`);
      }
    };

    const handleResize = () => handleScroll();

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    const timer = setTimeout(handleScroll, 0);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      clearTimeout(timer);
    };
  }, []);

  if (isLoading) {
    return (
      <Loader
        loaderContainerStyles={{
          top: 0,
          left: 0,
          background: "rgba(0, 0, 0, 0.7)",
        }}
      />
    );
  }

  return (
    <main className={styles.page}>
      <div className={styles.container}>
        <section className={styles.leftSection}>
          <StickyProfileHero
            collapsed={collapsed}
            cardRef={cardRef}
            userData={userData}
          />

          <div ref={contentRef} className={styles.scrollableContent}>
            <About userData={userData} />

            <Expertise userData={userData} />

            <ReviewsSection userData={userData} />
          </div>
        </section>

        <aside className={styles.rightSection}>
          <PricingCard userData={userData} />
        </aside>
      </div>
    </main>
  );
}
